import { MUTATION_RESPONSE_RECEIVED } from '@web-platform/shared-universal-constants/src/network';
import { PLACE_RESULT_FEATURE_TYPE } from '@web-platform/shared-universal-constants/src/placeResultFeatureType';
import {
  PLACE_FIELD_NAME,
  PLACE_FIELD_NAME_LIST,
  PLACE_FIELD_TYPE,
  PLACE_RESULT_TYPE,
} from '@web-platform/shared-universal-constants/src/places';
import { RADAR_CREATE_SESSIONS } from '@web-platform/shared-universal-types/src/network/requestTypes';
import { logOperationalEvent } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import {
  getValueOfUnion,
  getPlacesResponseBody,
  isPlacesResponseError,
} from '@web-platform/shared-universal-utils-frontend/src/helpers/radarPlacesResponse';
import keyBy from 'lodash/keyBy';

import { PLACE_RESULT_LOADED_EVENT_EMITTED } from '../../../actions/filters';

import { invertVibes } from './invertVibes';

import type { PlacesResponseBody } from '@web-platform/shared-universal-types/src/network/endpoints/radar/radarExplore';
import type { Action } from '@web-platform/shared-universal-types/src/redux/banana/actions/actions-banana-types';
import type { PlaceResultsState } from '@web-platform/shared-universal-types/src/redux/banana/state/combinedExplore/placeResults';
import type { Maybe } from '@web-platform/shared-universal-types-utils/src/utils';

export const INITIAL_STATE: PlaceResultsState = {
  searchType: null,
  byId: {},
  vibesById: {},
  filterPills: [],
  placeResultFeatures: {
    flightsIndicative: PLACE_RESULT_FEATURE_TYPE.AVAILABLE,
    hotelsIndicative: PLACE_RESULT_FEATURE_TYPE.DISABLED,
    images: PLACE_RESULT_FEATURE_TYPE.AVAILABLE,
    ads: PLACE_RESULT_FEATURE_TYPE.AVAILABLE,
  },
};

const getPlaceResults = (
  placesResponseBody: Maybe<PlacesResponseBody>,
  prevPlaceResults: PlaceResultsState,
): PlaceResultsState => {
  // return previous state because of response body error
  if (!placesResponseBody) return prevPlaceResults;

  const searchType = PLACE_FIELD_NAME_LIST.reduce(
    (placeSearchType: PlaceResultsState['searchType'], curPlaceFieldName) => {
      if (!placeSearchType && !!getValueOfUnion(placesResponseBody, curPlaceFieldName)) {
        return PLACE_FIELD_TYPE[curPlaceFieldName];
      }
      return placeSearchType;
    },
    null,
  );

  // return previous state because of search type error
  if (!searchType) return prevPlaceResults;

  const searchResult = getValueOfUnion(placesResponseBody, PLACE_FIELD_NAME[searchType]);

  // return previous state because of search result error
  if (!searchResult) return prevPlaceResults;

  const { features } = searchResult;

  if (features.flightsIndicative === PLACE_RESULT_FEATURE_TYPE.FAILED) {
    logOperationalEvent({
      component: 'combined_explore_radar_features',
      eventName: 'feature_failed',
      feature: 'flightsIndicative',
    });
  }

  if (features.hotelsIndicative === PLACE_RESULT_FEATURE_TYPE.FAILED) {
    logOperationalEvent({
      component: 'combined_explore_radar_features',
      eventName: 'feature_failed',
      feature: 'hotelsIndicative',
    });
  }

  if (features.images === PLACE_RESULT_FEATURE_TYPE.FAILED) {
    logOperationalEvent({
      component: 'combined_explore_radar_features',
      eventName: 'feature_failed',
      feature: 'images',
    });
  }

  if (features.ads === PLACE_RESULT_FEATURE_TYPE.FAILED) {
    logOperationalEvent({
      component: 'combined_explore_radar_features',
      eventName: 'feature_failed',
      feature: 'ads',
    });
  }

  const locationResults = (searchResult.results || []).filter(
    (result) => result.type === PLACE_RESULT_TYPE.LOCATION,
  );

  logOperationalEvent({
    component: 'combined_explore_place_card_prices',
    eventName: 'results_loaded',
    searchType,
    flightsIndicativeStatus: features.flightsIndicative ?? '',
    hotelsIndicativeStatus: features.hotelsIndicative ?? '',
    totalLocationCount: locationResults.length,
    noPriceFlightCount: locationResults.filter((result) => !result.content.flightQuotes).length,
    noPriceHotelCount: locationResults.filter((result) => !result.content.hotelQuotes).length,
  });

  const vibesById = invertVibes(placesResponseBody);

  const filterPills = searchResult.buckets?.length ? searchResult.buckets : [];

  // return new state
  return {
    searchType,
    byId: keyBy(searchResult.results || [], (place) => place.id),
    vibesById,
    filterPills,
    placeResultFeatures: searchResult.features,
    hotelMetadata: searchResult.hotelMetadata,
    resultLoadedEventHasEmitted: false,
  };
};

export default function (
  state: PlaceResultsState = INITIAL_STATE,
  action: Action,
): PlaceResultsState {
  switch (action.type) {
    case MUTATION_RESPONSE_RECEIVED: {
      const { payload } = action;
      if (payload.type === RADAR_CREATE_SESSIONS && !isPlacesResponseError(payload.response)) {
        const placesResponseBody = getPlacesResponseBody(payload.response);
        return getPlaceResults(placesResponseBody, state);
      }
      return state;
    }

    case PLACE_RESULT_LOADED_EVENT_EMITTED: {
      return {
        ...state,
        resultLoadedEventHasEmitted: true,
      };
    }

    default:
      return state;
  }
}

export const get = (state: PlaceResultsState): PlaceResultsState => state;
